import React from "react";
import { isSafeUrl } from "../../../utils/url-validation";
import type { ArticleAttachment } from "./article-attachment.types";

export type { ArticleAttachment };

/**
 * Badge labels shown in the frontend for each attachment type.
 * Note: these are display labels only — unrelated to the backend's
 * TYPE_FALLBACK_LABEL which is used as a fallback filename when the
 * original filename is not available.
 */
export const attachmentTypeLabels: Record<string, string> = {
  PDF: "PDF",
  DOCX: "DOCX",
  IMAGE: "Obraz",
  DOC: "DOC",
  XLS: "XLS",
  XLSX: "XLSX",
};

export interface ArticleAttachmentListProps {
  attachments: ArticleAttachment[];
  /** Used to build the accessible group label (falls back when omitted). */
  articleTitle?: string;
  /**
   * Root wrapper classes. Defaults to the compact `mt-2` used inside the call
   * article list; the recommendation "Kontekst" panel overrides this to match
   * the surrounding `<dl>` row rhythm (`gap-y-4`).
   */
  className?: string;
  /**
   * Heading classes. Defaults to the compact `text-xs` sub-text used in the
   * article list; the "Kontekst" panel overrides to `text-sm` so the heading
   * reads as a peer of the sibling `<dt>` field labels, not a subordinate.
   */
  headingClassName?: string;
}

/**
 * Renders the safe-URL-filtered list of attachments for an article.
 * Filters to HTTPS-only URLs and renders each as a link with a type badge.
 */
export const ArticleAttachmentList: React.FC<ArticleAttachmentListProps> = ({
  attachments,
  articleTitle,
  className = "mt-2",
  headingClassName = "text-xs font-medium text-neutral-500 text-left",
}) => {
  const safeAttachments = (attachments ?? []).filter((att) =>
    isSafeUrl(att.url, { requireHttps: true }),
  );

  if (safeAttachments.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={
        articleTitle
          ? `Załączniki artykułu: ${articleTitle}`
          : "Załączniki artykułu"
      }
      className={className}
    >
      <p className={headingClassName}>
        Załączniki ({safeAttachments.length})
      </p>
      <ul className="mt-1 space-y-1">
        {safeAttachments.map((att, idx) => {
          const typeLabel = attachmentTypeLabels[att.type] || att.type;
          return (
            <li
              key={`${att.url}-${idx}`}
              className="flex items-center gap-2 min-w-0"
            >
              <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-700 uppercase">
                {typeLabel}
              </span>
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={att.filename}
                aria-label={`Otwórz załącznik ${att.filename} (${typeLabel})`}
                className="text-xs text-primary-600 hover:text-primary-700 hover:underline truncate text-left"
              >
                {att.filename}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ArticleAttachmentList;
